import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { checkAndRefreshToken } from '../utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, FileText, Calendar, Clock, MapPin, 
  ChevronDown, ChevronUp, Edit, Trash2, Eye, EyeOff,
  Save, X, Target, Loader2, Share2, MessageSquare, Download,
  CheckSquare, Square, FileDown, User, History, Copy, 
  BarChart3, PauseCircle, PlayCircle, RefreshCw, Sparkles, Send,
  Camera, ImageIcon
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import AIFeedbackPopup, { shouldShowFeedback } from '@/components/AIFeedbackPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const SESSION_TYPES = [
  { value: 'individual', label: 'Individual Therapy' },
  { value: 'group', label: 'Group Therapy' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'parent_training', label: 'Parent Training' },
  { value: 'screening', label: 'Screening' },
  { value: 'evaluation', label: 'Evaluation' }
];

const SESSION_LOCATIONS = [
  { value: 'clinic', label: 'Clinic' },
  { value: 'home', label: 'Home Visit' },
  { value: 'school', label: 'School' },
  { value: 'teletherapy', label: 'Teletherapy' },
  { value: 'community', label: 'Community Setting' }
];

const CATEGORIES = [
  { value: 'general', label: 'General', color: 'bg-stone-100 text-stone-700' },
  { value: 'language', label: 'Language', color: 'bg-blue-100 text-blue-700' },
  { value: 'prelinguistic', label: 'Prelinguistic Skills', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'articulation', label: 'Articulation', color: 'bg-green-100 text-green-700' },
  { value: 'fluency', label: 'Fluency', color: 'bg-purple-100 text-purple-700' },
  { value: 'pragmatics', label: 'Pragmatics', color: 'bg-pink-100 text-pink-700' },
  { value: 'auditory', label: 'Auditory Processing', color: 'bg-violet-100 text-violet-700' },
  { value: 'literacy', label: 'Literacy', color: 'bg-rose-100 text-rose-700' },
  { value: 'feeding', label: 'Feeding', color: 'bg-orange-100 text-orange-700' },
  { value: 'aac', label: 'AAC', color: 'bg-teal-100 text-teal-700' },
  { value: 'motor_planning', label: 'Motor Planning', color: 'bg-fuchsia-100 text-fuchsia-700' },
  { value: 'voice', label: 'Voice', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'cognitive', label: 'Cognitive-Communication', color: 'bg-amber-100 text-amber-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

// Maps a SOAP focus area (the chips at the top of the SOAP form) to the
// goal-category slugs used on the Child Profile. Used to filter the
// "Suggested goals from profile" list so the therapist only sees goals
// relevant to the focus areas they just picked.
//
// Categories empty or matching 'general' / 'other' → no filter (show all).
const SOAP_TO_GOAL_CATEGORIES = {
  language:       ['expressive', 'receptive'],
  prelinguistic:  ['expressive', 'social_emotional'],
  articulation:   ['speech_motor'],
  fluency:        ['fluency'],
  pragmatics:     ['pragmatic', 'social_emotional'],
  auditory:       ['auditory_processing', 'receptive'],
  literacy:       ['literacy'],
  feeding:        ['feeding'],
  aac:            ['aac'],
  motor_planning: ['speech_motor'],
  voice:          ['speech_motor'],
  cognitive:      ['expressive', 'receptive'],
  other:          ['other'],
};

const PROMPTING_LEVELS = [
  { value: 'none', label: 'Not recorded' },
  { value: 'independent', label: 'Independent' },
  { value: 'minimal', label: 'Minimal Cueing' },
  { value: 'moderate', label: 'Moderate Cueing' },
  { value: 'maximum', label: 'Maximum Cueing' }
];

const COMMON_STRATEGIES = [
  'Modeling', 'Expansion', 'Recasting', 'Wait time', 'Visual supports',
  'Choice making', 'Communicative temptations', 'Environmental arrangement', 'Aided language stimulation',
  'Natural gestalts', 'Following child\'s lead', 'Joint attention activities', 'Other'
];

// Generate printable HTML for a single session note
const generateSingleNoteHtml = (note, child, therapistRegNumber) => {
  const categories = (note.categories || [note.category]).map(cat => {
    const found = CATEGORIES.find(c => c.value === cat);
    return found ? found.label : cat;
  }).join(', ');
  
  const sessionType = SESSION_TYPES.find(t => t.value === note.session_type)?.label || note.session_type;
  const location = SESSION_LOCATIONS.find(l => l.value === note.session_location)?.label || note.session_location;
  
  return `
    <div class="note-page">
      <div class="header">
        <h1>Session Note</h1>
        <div class="subtitle">${child?.name} • ${new Date(note.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        ${therapistRegNumber ? `<div class="reg-number">Registration: ${therapistRegNumber}</div>` : ''}
      </div>
      
      <div class="meta-grid">
        <div class="meta-item">
          <div class="meta-label">Session Type</div>
          <div class="meta-value">${sessionType}</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Duration</div>
          <div class="meta-value">${note.session_duration || 30} minutes</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Location</div>
          <div class="meta-value">${location}</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Focus Area(s)</div>
          <div class="meta-value">${categories}${note.other_category ? ` (${note.other_category})` : ''}</div>
        </div>
      </div>
      
      ${note.subjective ? `
      <div class="soap-section">
        <div class="soap-header">
          <div class="soap-letter soap-s">S</div>
          <div class="soap-title">Subjective</div>
        </div>
        <div class="soap-content soap-s-bg">${note.subjective}</div>
      </div>
      ` : ''}
      
      ${note.objective || note.observation ? `
      <div class="soap-section">
        <div class="soap-header">
          <div class="soap-letter soap-o">O</div>
          <div class="soap-title">Objective</div>
        </div>
        <div class="soap-content soap-o-bg">${note.objective || note.observation}</div>
      </div>
      ` : ''}
      
      ${note.assessment ? `
      <div class="soap-section">
        <div class="soap-header">
          <div class="soap-letter soap-a">A</div>
          <div class="soap-title">Assessment</div>
        </div>
        <div class="soap-content soap-a-bg">${note.assessment}</div>
      </div>
      ` : ''}
      
      ${note.plan ? `
      <div class="soap-section">
        <div class="soap-header">
          <div class="soap-letter soap-p">P</div>
          <div class="soap-title">Plan</div>
        </div>
        <div class="soap-content soap-p-bg">${note.plan}</div>
      </div>
      ` : ''}
      
      ${(note.prompting_level && note.prompting_level !== 'none') || note.accuracy_data || note.goals_addressed?.length > 0 || note.strategies_used?.length > 0 ? `
      <div class="clinical-section">
        <div class="clinical-title">Clinical Data</div>
        ${note.prompting_level && note.prompting_level !== 'none' ? `<p><strong>Prompting Level:</strong> ${note.prompting_level}</p>` : ''}
        ${note.accuracy_data ? `<p><strong>Accuracy:</strong> ${note.accuracy_data}</p>` : ''}
        ${note.goals_addressed?.length > 0 ? `
          <p style="margin-top: 8px;"><strong>Goals Addressed:</strong></p>
          <div class="tags">${note.goals_addressed.map(g => `<span class="tag">${g}</span>`).join('')}</div>
        ` : ''}
        ${note.strategies_used?.length > 0 ? `
          <p style="margin-top: 8px;"><strong>Strategies Used:</strong></p>
          <div class="tags">${note.strategies_used.map(s => `<span class="tag">${s}</span>`).join('')}</div>
        ` : ''}
        ${note.materials_used?.length > 0 ? `
          <p style="margin-top: 8px;"><strong>Materials Used:</strong></p>
          <div class="tags">${note.materials_used.map(m => `<span class="tag">${m}</span>`).join('')}</div>
        ` : ''}
      </div>
      ` : ''}
      
      ${(note.treatment_codes?.length > 0 || note.cpt_code) ? `
      <div class="clinical-section">
        <div class="clinical-title">Treatment/Billing Codes</div>
        <div class="tags">${(note.treatment_codes?.length > 0 ? note.treatment_codes : [note.cpt_code]).filter(Boolean).map(c => `<span class="tag">${c}</span>`).join('')}</div>
        ${note.billing_notes ? `<p style="margin-top: 8px;"><strong>Notes:</strong> ${note.billing_notes}</p>` : ''}
      </div>
      ` : ''}
      
      ${note.parent_summary ? `
      <div class="parent-section">
        <div class="parent-title">Parent/Caregiver Summary${note.shared_with_parent ? ' (Shared)' : ' (Not Shared)'}</div>
        <p>${note.parent_summary}</p>
      </div>
      ` : ''}
    </div>
  `;
};

// Generate printable HTML for multiple notes (bulk export)
const generateBulkPrintHtml = (notes, child, therapistRegNumber) => {
  const notesHtml = notes.map(note => generateSingleNoteHtml(note, child, therapistRegNumber)).join('<div class="page-break"></div>');
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Session Notes - ${child?.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: Georgia, 'Times New Roman', serif; 
          max-width: 800px; 
          margin: 0 auto; 
          padding: 40px; 
          color: #1f2937;
          line-height: 1.6;
        }
        .note-page {
          margin-bottom: 40px;
        }
        .page-break {
          page-break-after: always;
          border-top: 2px dashed #e5e7eb;
          margin: 40px 0;
        }
        .header { 
          text-align: center; 
          margin-bottom: 30px; 
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 20px;
        }
        .header h1 { 
          font-size: 24px; 
          color: #4f46e5; 
          margin-bottom: 8px;
        }
        .header .subtitle { 
          font-size: 14px; 
          color: #6b7280;
        }
        .header .reg-number {
          font-size: 12px;
          color: #4f46e5;
          margin-top: 8px;
          font-weight: 500;
        }
        .meta-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 30px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .meta-item {
          text-align: center;
        }
        .meta-label {
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .meta-value {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }
        .soap-section {
          margin-bottom: 24px;
        }
        .soap-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .soap-letter {
          width: 28px;
          height: 28px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
        }
        .soap-s { background: #dbeafe; color: #1d4ed8; }
        .soap-o { background: #dcfce7; color: #15803d; }
        .soap-a { background: #fef3c7; color: #b45309; }
        .soap-p { background: #f3e8ff; color: #7c3aed; }
        .soap-title {
          font-size: 16px;
          font-weight: 600;
        }
        .soap-content {
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 4px solid #e5e7eb;
          white-space: pre-wrap;
        }
        .soap-content.soap-s-bg { border-left-color: #3b82f6; }
        .soap-content.soap-o-bg { border-left-color: #22c55e; }
        .soap-content.soap-a-bg { border-left-color: #f59e0b; }
        .soap-content.soap-p-bg { border-left-color: #8b5cf6; }
        .clinical-section {
          margin-top: 30px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .clinical-title {
          font-size: 14px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 12px;
        }
        .tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        .tag {
          padding: 4px 12px;
          background: #e5e7eb;
          border-radius: 16px;
          font-size: 12px;
        }
        .parent-section {
          margin-top: 30px;
          padding: 16px;
          background: #eff6ff;
          border-radius: 8px;
          border: 1px solid #bfdbfe;
        }
        .parent-title {
          font-size: 14px;
          font-weight: 600;
          color: #1d4ed8;
          margin-bottom: 8px;
        }
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #9ca3af;
          text-align: center;
        }
        @media print {
          body { padding: 20px; }
          .no-print { display: none; }
          .page-break { page-break-after: always; border: none; margin: 0; }
        }
      </style>
    </head>
    <body>
      ${notesHtml}
      <div class="footer">
        Generated from PlayFirst • ${new Date().toLocaleDateString()} • ${notes.length} session note${notes.length !== 1 ? 's' : ''}
      </div>
    </body>
    </html>
  `;
};

export default function SessionNotes({ onLogout }) {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [notes, setNotes] = useState([]);
  // Post-save: activity suggestions modal for jump-to-Weekly-Focus
  const [suggestModal, setSuggestModal] = useState({ open: false, items: [], childId: null });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Bulk selection
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // Post-save "send feedback to parent now?" prompt — opens with the
  // just-saved note pre-selected so the therapist can quickly add older
  // notes and trigger the existing multi-select feedback flow.
  const [postSaveShareModal, setPostSaveShareModal] = useState({
    open: false, noteId: null,
  });
  
  // Therapist registration number
  const [therapistRegNumber, setTherapistRegNumber] = useState('');
  const [showRegNumberDialog, setShowRegNumberDialog] = useState(false);
  const [tempRegNumber, setTempRegNumber] = useState('');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    session_time: '',  // Time for timetable view
    session_type: 'individual',
    session_duration: 30,
    session_location: 'clinic',
    categories: ['language'], // Changed to array for multi-select
    other_category: '', // For custom "other" category
    // SOAP Note
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    // Goal tracking
    goals_addressed: [],
    goal_progress: {},
    // Clinical data
    prompting_level: 'none',
    accuracy_data: '',
    materials_used: [],
    strategies_used: [],
    // Billing
    cpt_code: '',
    treatment_codes: [],
    billing_notes: '',
    // Parent sharing
    shared_with_parent: false,
    parent_summary: '',
    // Draft
    is_draft: false
  });

  const [newMaterial, setNewMaterial] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [customStrategy, setCustomStrategy] = useState('');
  
  // Previous session goals
  const [previousGoals, setPreviousGoals] = useState(null);
  const [loadingPreviousGoals, setLoadingPreviousGoals] = useState(false);
  const [showPreviousGoals, setShowPreviousGoals] = useState(false);
  const [carryOverGoals, setCarryOverGoals] = useState(true); // Auto carry-over option
  // Feb 2026 — override for the focus-area filter on the "From profile"
  // panel. Off = only show goals matching selected focus area(s);
  // on = show every goal on the child's profile regardless of category.
  const [showAllProfileGoals, setShowAllProfileGoals] = useState(false);
  const [carriedGoals, setCarriedGoals] = useState([]); // previous-session goals offered as suggestions
  
  // Draft and auto-save
  const [draftId, setDraftId] = useState(null);
  const autoSaveTimerRef = React.useRef(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const formDataRef = React.useRef(formData);
  // Feb 2026 (SOAP save reliability) — synchronous guard used to stop the
  // 30-second auto-save interval from silently reverting a note back to
  // draft while a final Save/Complete is in flight, and to prevent a
  // double-click on Save from creating duplicate rows. Refs update
  // synchronously (unlike useState), so setting this at the very top of
  // handleSubmit is race-safe.
  const finalizingRef = React.useRef(false);
  
  // Goal stats
  const [goalStats, setGoalStats] = useState(null);
  const [showGoalStats, setShowGoalStats] = useState(false);
  
  // Parent summary generation
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [showParentFeedback, setShowParentFeedback] = useState(false);

  // Multi-note (bulk) parent feedback generation
  const [multiFeedback, setMultiFeedback] = useState(null); // { summary, session_count, date_range, audience }
  const [showMultiFeedback, setShowMultiFeedback] = useState(false);
  const [multiAudience, setMultiAudience] = useState('parent'); // 'parent' | 'whatsapp' | 'team'
  const [generatingMulti, setGeneratingMulti] = useState(false);
  const [sendingMulti, setSendingMulti] = useState(false);

  // Feb 2026 — Session Photo Sharing (independent of written feedback).
  // Staging tray lives for the lifetime of the share dialog; each entry
  // is { id, mime, size, created_at, previewUrl } where previewUrl is a
  // local blob: URL used only for the therapist's own preview (NEVER
  // served to the parent). Server-side storage is private.
  const [sessionPhotos, setSessionPhotos] = useState([]); // staged (not yet shared)
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoConsent, setPhotoConsent] = useState({ loaded: false, has_consent: false, consent_text: '' });
  const [photoConsentDialog, setPhotoConsentDialog] = useState(false);
  const [photoConsentAck, setPhotoConsentAck] = useState(false);
  const photoFileInputRef = useRef(null);

  // Load therapist photo consent + any already-staged photos whenever the
  // SOAP form or the share dialog is open for this child. Both surfaces
  // read from the same staging tray.
  useEffect(() => {
    const needs = (showMultiFeedback || showForm) && !editingNote;
    if (!needs || !childId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const [cRes, sRes] = await Promise.all([
          axios.get(`${API}/session-photos/therapist-consent/${childId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/session-photos/staging/${childId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (cancelled) return;
        setPhotoConsent({ loaded: true, ...cRes.data });
        setSessionPhotos((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev];
          for (const p of (sRes.data?.photos || [])) {
            if (!seen.has(p.id)) merged.push({ ...p, previewUrl: null });
          }
          return merged;
        });
      } catch (err) {
        if (!cancelled) setPhotoConsent({ loaded: true, has_consent: false, consent_text: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [showMultiFeedback, showForm, editingNote, childId]);

  const confirmPhotoConsent = async () => {
    if (!photoConsentAck) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/session-photos/therapist-consent`,
        { child_id: childId, acknowledged: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPhotoConsent({ loaded: true, has_consent: true, consent_text: photoConsent.consent_text || '', version: res.data?.version });
      setPhotoConsentDialog(false);
      setPhotoConsentAck(false);
      // Immediately open the file picker so the therapist's click isn't wasted.
      setTimeout(() => photoFileInputRef.current?.click(), 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record consent');
    }
  };

  const onAddPhotosClick = () => {
    if (!photoConsent.has_consent) {
      setPhotoConsentDialog(true);
      return;
    }
    photoFileInputRef.current?.click();
  };

  const handlePhotoFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // reset so the same file can be picked again later
    if (!files.length) return;
    const remainingSlots = 5 - sessionPhotos.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum 5 photos per session update.');
      return;
    }
    const toUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.info(`Only ${remainingSlots} more photo${remainingSlots > 1 ? 's' : ''} can be added.`);
    }
    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      for (const f of toUpload) {
        const fd = new FormData();
        fd.append('child_id', childId);
        fd.append('file', f);
        try {
          const res = await axios.post(`${API}/session-photos/upload`, fd, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          const previewUrl = URL.createObjectURL(f);
          setSessionPhotos((prev) => [
            ...prev,
            { ...res.data, previewUrl },
          ]);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
        }
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removeStagedPhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/session-photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessionPhotos((prev) => {
        const removed = prev.find((p) => p.id === photoId);
        if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
        return prev.filter((p) => p.id !== photoId);
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove photo');
    }
  };

  // Reset the staging tray whenever BOTH the SOAP form and the share
  // dialog are closed. Keeping staged photos while either is open lets a
  // therapist upload during the SOAP write and then send from the share
  // dialog without losing their uploads.
  useEffect(() => {
    if (showMultiFeedback || showForm) return;
    sessionPhotos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setSessionPhotos([]);
    setPhotoConsentAck(false);
  }, [showMultiFeedback, showForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateMultiNoteFeedback = async (audience = multiAudience) => {
    if (selectedNotes.size === 0) {
      toast.error('Select at least one session note first');
      return;
    }
    // Feb 2026 — open the dialog immediately so the photo tray is visible
    // right away. The AI feedback area shows a loading state while we
    // fetch the recap; a therapist who only wants to send photos can start
    // adding them without waiting for AI.
    setShowMultiFeedback(true);
    setMultiAudience(audience);
    setGeneratingMulti(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/progress/multi-summary`,
        {
          child_id: childId,
          note_ids: Array.from(selectedNotes),
          audience,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMultiFeedback(response.data);
      setMultiAudience(response.data.audience || audience);
    } catch (error) {
      console.error('Failed to generate multi-note feedback:', error);
      toast.error(error?.response?.data?.detail || 'Failed to generate feedback');
    } finally {
      setGeneratingMulti(false);
    }
  };

  const copyMultiFeedback = async () => {
    if (!multiFeedback?.summary) return;
    try {
      await navigator.clipboard.writeText(multiFeedback.summary);
      toast.success('Copied to clipboard');
    } catch (_) {
      toast.error('Could not copy');
    }
  };

  const sendMultiFeedbackToParents = async () => {
    const message = (multiFeedback?.summary || '').trim();
    const photoIds = sessionPhotos.map((p) => p.id);
    // Feb 2026 — Photos and feedback are independent. Allow:
    //  (feedback only) | (photos only) | (feedback + photos)
    if (!message && photoIds.length === 0) {
      toast.error('Nothing to send — add feedback or at least one photo first');
      return;
    }
    const parentIds = child?.parent_ids || [];
    if (!parentIds.length) {
      toast.error('No parent linked to this child yet');
      return;
    }
    const sessionCount = multiFeedback?.session_count || selectedNotes.size;
    const range = multiFeedback?.date_range || {};
    const titleFromRange = sessionCount
      ? (range.start && range.end
        ? `Recap of ${sessionCount} sessions (${range.start} → ${range.end})`
        : `Recap of ${sessionCount} sessions`)
      : 'Session update';
    const parts = [];
    if (message) parts.push('a feedback summary');
    if (photoIds.length) parts.push(`${photoIds.length} photo${photoIds.length > 1 ? 's' : ''}`);
    const confirmLabel = parts.join(' and ') || 'this update';
    if (!window.confirm(`Send ${confirmLabel} to ${parentIds.length} parent${parentIds.length > 1 ? 's' : ''}?`)) {
      return;
    }
    setSendingMulti(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/general-feedback`,
        {
          child_id: childId,
          message,
          title: titleFromRange.slice(0, 120),
          photo_ids: photoIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sent to parent(s)');
      // Clear local preview blobs; staged photos are now shared on the server.
      sessionPhotos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setSessionPhotos([]);
      setShowMultiFeedback(false);
      setSelectMode(false);
      setSelectedNotes(new Set());
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingMulti(false);
    }
  };

  const generateParentSummary = async () => {
    setGeneratingSummary(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/progress/generate-parent-summary`, {
        child_id: childId,
        subjective: formData.subjective,
        objective: formData.objective,
        assessment: formData.assessment,
        plan: formData.plan,
        goals_addressed: formData.goals_addressed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({ ...prev, parent_summary: response.data.summary }));
      if (shouldShowFeedback('parent_summary')) {
        setShowParentFeedback(true);
      }
      toast.success('Summary generated! Feel free to edit it.');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  useEffect(() => {
    fetchData();
    loadTherapistRegNumber();
    fetchGoalStats();
  }, [childId]);

  // Keep formDataRef in sync without triggering re-renders
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Token refresh when form is open (every 15 minutes)
  useEffect(() => {
    if (showForm) {
      checkAndRefreshToken();
      
      const tokenRefreshInterval = setInterval(() => {
        checkAndRefreshToken();
      }, 15 * 60 * 1000);
      
      return () => clearInterval(tokenRefreshInterval);
    }
  }, [showForm]);

  // Auto-save: trigger 30s after form opens or after each save, not on every keystroke
  useEffect(() => {
    if (!showForm || editingNote) return;

    // Feb 2026 — store the interval id in the shared ref so
    // handleSubmit can synchronously kill it the moment the therapist
    // clicks Save/Complete (avoids the stale-autosave race that was
    // reverting completed notes back to draft).
    const intervalId = setInterval(() => {
      autoSaveDraft();
    }, 30000);
    autoSaveTimerRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      if (autoSaveTimerRef.current === intervalId) {
        autoSaveTimerRef.current = null;
      }
    };
  }, [showForm, editingNote, draftId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const autoSaveDraft = async () => {
    // Feb 2026 — never auto-save while the therapist is finalizing the
    // note; a stale draft PUT arriving after Save/Complete would silently
    // revert is_draft back to true (the "note saved as draft, had to save
    // again" bug). finalizingRef flips synchronously so this guard is
    // race-safe even before setSaving(true) reflects in React state.
    if (finalizingRef.current) return;
    const currentFormData = formDataRef.current;
    // Only auto-save if form has meaningful content
    const hasContent = currentFormData.subjective || currentFormData.objective || 
                       currentFormData.assessment || currentFormData.plan || 
                       currentFormData.goals_addressed.length > 0;
    
    if (!hasContent || editingNote || savingDraft) return;
    
    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...currentFormData,
        child_id: childId,
        is_draft: true
      };
      
      const axiosConfig = { headers, timeout: 15000 };
      
      if (draftId) {
        await axios.put(`${API}/progress/${draftId}`, payload, axiosConfig);
      } else {
        const response = await axios.post(`${API}/progress`, payload, axiosConfig);
        setDraftId(response.data.id);
      }
      
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  const saveDraftManually = async () => {
    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please log in again.');
        setSavingDraft(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        child_id: childId,
        is_draft: true
      };
      
      const axiosConfig = { headers, timeout: 15000 };
      
      if (draftId) {
        await axios.put(`${API}/progress/${draftId}`, payload, axiosConfig);
      } else {
        const response = await axios.post(`${API}/progress`, payload, axiosConfig);
        setDraftId(response.data.id);
      }
      
      setLastSaved(new Date());
      toast.success('Draft saved! You can safely exit and return later.');
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Save timed out. Please try again.');
      } else {
        toast.error('Failed to save draft');
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const resumeDraft = async (draft) => {
    setFormData({
      date: draft.date || new Date().toISOString().split('T')[0],
      session_time: draft.session_time || '',
      session_type: draft.session_type || 'individual',
      session_duration: draft.session_duration || 30,
      session_location: draft.session_location || 'clinic',
      categories: draft.categories || ['language'],
      other_category: draft.other_category || '',
      subjective: draft.subjective || '',
      objective: draft.objective || '',
      assessment: draft.assessment || '',
      plan: draft.plan || '',
      goals_addressed: draft.goals_addressed || [],
      goal_progress: draft.goal_progress || {},
      prompting_level: draft.prompting_level || 'none',
      accuracy_data: draft.accuracy_data || '',
      materials_used: draft.materials_used || [],
      strategies_used: draft.strategies_used || [],
      cpt_code: draft.cpt_code || '',
      treatment_codes: draft.treatment_codes || [],
      billing_notes: draft.billing_notes || '',
      shared_with_parent: draft.shared_with_parent || false,
      parent_summary: draft.parent_summary || '',
      is_draft: true
    });
    setDraftId(draft.id);
    setShowForm(true);
    toast.info('Resumed draft from ' + new Date(draft.updated_at || draft.created_at).toLocaleString());
  };

  const fetchGoalStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/progress/${childId}/goal-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoalStats(response.data);
    } catch (error) {
      console.log('Could not load goal stats');
    }
  };

  const loadTherapistRegNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/therapist/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.registration_number) {
        setTherapistRegNumber(response.data.registration_number);
      }
    } catch (error) {
      // Profile endpoint may not exist yet, that's okay
      console.log('Could not load therapist profile');
    }
  };

  const saveTherapistRegNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/therapist/profile`, 
        { registration_number: tempRegNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTherapistRegNumber(tempRegNumber);
      setShowRegNumberDialog(false);
      toast.success('Registration number saved!');
    } catch (error) {
      toast.error('Failed to save registration number');
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [childRes, notesRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, { headers }),
        axios.get(`${API}/progress/${childId}`, { headers })
      ]);
      
      setChild(childRes.data);
      setNotes(notesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousGoals = async () => {
    setLoadingPreviousGoals(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/progress/${childId}/previous-goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviousGoals(response.data);
      setShowPreviousGoals(true);
    } catch (error) {
      toast.error('Failed to load previous goals');
    } finally {
      setLoadingPreviousGoals(false);
    }
  };

  const copyGoalToForm = (goal) => {
    if (!formData.goals_addressed.includes(goal)) {
      setFormData(prev => ({
        ...prev,
        goals_addressed: [...prev.goals_addressed, goal]
      }));
      toast.success('Goal added to current session');
    } else {
      toast.info('Goal already added');
    }
  };

  const copyAllGoalsToForm = () => {
    if (previousGoals?.goals?.length > 0) {
      const newGoals = previousGoals.goals.filter(g => !formData.goals_addressed.includes(g));
      if (newGoals.length > 0) {
        setFormData(prev => ({
          ...prev,
          goals_addressed: [...prev.goals_addressed, ...newGoals]
        }));
        toast.success(`${newGoals.length} goal(s) added to current session`);
      } else {
        toast.info('All goals already added');
      }
    }
  };

  const openNewSessionForm = async () => {
    resetForm();
    setShowForm(true);

    // Auto-carry treatment codes from the most recent finalized note for this
    // child. Therapists usually bill the same code week-to-week — saves typing
    // and they can still override per-session.
    try {
      const mostRecent = (notes || []).find(
        (n) => !n.is_draft && (n.treatment_codes?.length || n.cpt_code),
      );
      if (mostRecent) {
        setFormData((prev) => ({
          ...prev,
          treatment_codes: mostRecent.treatment_codes?.length
            ? [...mostRecent.treatment_codes]
            : prev.treatment_codes,
          cpt_code: mostRecent.cpt_code || prev.cpt_code,
          session_type: mostRecent.session_type || prev.session_type,
        }));
      }
    } catch {
      // Non-critical — silently continue if there are no prior notes.
    }

    // Auto-fetch and carry over previous goals if enabled
    if (carryOverGoals) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/progress/${childId}/previous-goals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.has_previous && response.data.goals?.length > 0) {
          // Offer previous goals as focus-filtered suggestions instead of
          // pre-selecting every goal (which bypassed the focus-area filter).
          setCarriedGoals([...response.data.goals]);
          setPreviousGoals(response.data);
        }
      } catch (error) {
        // Silently fail - not critical
        console.log('Could not auto-carry goals');
      }
    }
  };

  const resetForm = () => {
    setShowAllProfileGoals(false);
    setCarriedGoals([]);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      session_time: '',
      session_type: 'individual',
      session_duration: 30,
      session_location: 'clinic',
      categories: ['language'],
      other_category: '',
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      goals_addressed: [],
      goal_progress: {},
      prompting_level: 'none',
      accuracy_data: '',
      materials_used: [],
      strategies_used: [],
      cpt_code: '',
      billing_notes: '',
      shared_with_parent: false,
      parent_summary: '',
      is_draft: false
    });
    setEditingNote(null);
    setDraftId(null);
    setLastSaved(null);
    setPreviousGoals(null);
    setShowPreviousGoals(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Feb 2026 (SOAP save reliability):
    //  1) Reject a double-click BEFORE React state has a chance to flip
    //     `saving` (state updates are async — a rapid second click would
    //     otherwise slip through and create/finalize twice).
    //  2) Immediately kill the auto-save interval so a 30s tick can't
    //     land AFTER we PUT is_draft=false and silently revert the note
    //     back to draft.
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    setSaving(true);
    
    try {
      // Refresh token before saving - with timeout to prevent hanging
      try {
        const refreshPromise = checkAndRefreshToken();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token refresh timeout')), 5000)
        );
        await Promise.race([refreshPromise, timeoutPromise]);
      } catch (refreshError) {
        console.warn('Token refresh failed, continuing with existing token:', refreshError);
        // Continue with existing token - don't block the save
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please log in again.');
        setSaving(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        ...formData,
        child_id: childId,
        category: formData.categories[0] || 'general', // For backwards compatibility
        observation: formData.objective, // For backwards compatibility
        is_draft: false // Mark as complete when saving
      };
      
      // Add timeout to the save request
      const axiosConfig = { 
        headers,
        timeout: 30000 // 30 second timeout
      };
      
      let savedNoteId = null;
      if (editingNote) {
        await axios.put(`${API}/progress/${editingNote.id}`, payload, axiosConfig);
        toast.success('Session note updated!');
        savedNoteId = editingNote.id;
      } else if (draftId) {
        // Finalize existing draft
        await axios.put(`${API}/progress/${draftId}`, payload, axiosConfig);
        toast.success('Session note saved!');
        savedNoteId = draftId;
      } else {
        const created = await axios.post(`${API}/progress`, payload, axiosConfig);
        toast.success('Session note created!');
        savedNoteId = created?.data?.id;
      }

      setShowForm(false);
      setDraftId(null);
      setLastSaved(null);
      resetForm();
      fetchData();
      fetchGoalStats();

      // After a fresh save (not edit), fetch activity suggestions so the
      // therapist can jump straight into authoring a Weekly Focus.
      if (savedNoteId && !editingNote) {
        // Open the "send feedback to parent?" prompt — the therapist can
        // dismiss it or jump into the multi-select share flow with the
        // just-saved note pre-selected.
        setPostSaveShareModal({ open: true, noteId: savedNoteId });
        try {
          const [sug, focusHist] = await Promise.all([
            axios.get(`${API}/activities/suggest-from-note/${savedNoteId}?limit=6`, axiosConfig),
            // Fetch the latest weekly focus so we can show the therapist
            // when they last sent one. Best-effort; an empty/missing
            // result simply suppresses the line.
            axios.get(`${API}/weekly-focus/${childId}`, axiosConfig).catch(() => null),
          ]);
          setSuggestModal({
            open: true,
            items: sug?.data?.suggestions || [],
            childId: sug?.data?.child_id || childId,
            noteId: savedNoteId,
            lastFocusAt: focusHist?.data?.created_at || null,
          });
        } catch (err) {
          console.warn('Activity suggestions failed:', err);
          setSuggestModal({
            open: true,
            items: [],
            childId,
            noteId: savedNoteId,
            lastFocusAt: null,
          });
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Save timed out. Please try again.');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save session note');
      }
    } finally {
      setSaving(false);
      // Feb 2026 — always release the finalize latch so the therapist
      // can retry if the request failed (network error, timeout, 401).
      finalizingRef.current = false;
    }
  };

  const startEdit = (note) => {
    setFormData({
      date: note.date || '',
      session_time: note.session_time || '',
      session_type: note.session_type || 'individual',
      session_duration: note.session_duration || 30,
      session_location: note.session_location || 'clinic',
      categories: note.categories || [note.category || 'general'],
      other_category: note.other_category || '',
      subjective: note.subjective || '',
      objective: note.objective || note.observation || '',
      assessment: note.assessment || '',
      plan: note.plan || '',
      goals_addressed: note.goals_addressed || [],
      goal_progress: note.goal_progress || {},
      prompting_level: note.prompting_level || 'none',
      accuracy_data: note.accuracy_data || '',
      materials_used: note.materials_used || [],
      strategies_used: note.strategies_used || [],
      cpt_code: note.cpt_code || '',
      treatment_codes: note.treatment_codes || [],
      billing_notes: note.billing_notes || '',
      shared_with_parent: note.shared_with_parent || false,
      parent_summary: note.parent_summary || ''
    });
    setEditingNote(note);
    setShowForm(true);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this session note? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/progress/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session note deleted');
      setSelectedNotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(noteId);
        return newSet;
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const downloadNote = (note) => {
    const html = generateBulkPrintHtml([note], child, therapistRegNumber);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const downloadSelectedNotes = () => {
    if (selectedNotes.size === 0) {
      toast.error('Please select at least one note to export');
      return;
    }
    
    const selectedNotesData = notes.filter(n => selectedNotes.has(n.id))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    const html = generateBulkPrintHtml(selectedNotesData, child, therapistRegNumber);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const toggleShare = async (note) => {
    if (!note || !note.id) {
      toast.error('Unable to update sharing - note data missing');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const newShareState = !note.shared_with_parent;
      
      // Show immediate feedback
      toast.loading('Updating sharing...', { id: 'share-toggle' });
      
      await axios.put(
        `${API}/progress/${note.id}/share`,
        { 
          shared_with_parent: newShareState,
          parent_summary: note.parent_summary || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(newShareState ? 'Note shared with parent' : 'Note hidden from parent', { id: 'share-toggle' });
      fetchData();
    } catch (error) {
      console.error('Toggle share error:', error);
      toast.error('Failed to update sharing - please try again', { id: 'share-toggle' });
    }
  };

  const toggleNoteSelection = (noteId) => {
    setSelectedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const selectAllNotes = () => {
    if (selectedNotes.size === notes.length) {
      setSelectedNotes(new Set());
    } else {
      setSelectedNotes(new Set(notes.map(n => n.id)));
    }
  };

  const toggleCategory = (categoryValue) => {
    setFormData(prev => {
      const categories = prev.categories || [];
      if (categories.includes(categoryValue)) {
        return { ...prev, categories: categories.filter(c => c !== categoryValue) };
      } else {
        return { ...prev, categories: [...categories, categoryValue] };
      }
    });
  };

  const addMaterial = () => {
    if (newMaterial.trim()) {
      setFormData(prev => ({
        ...prev,
        materials_used: [...prev.materials_used, newMaterial.trim()]
      }));
      setNewMaterial('');
    }
  };

  const removeMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      materials_used: prev.materials_used.filter((_, i) => i !== index)
    }));
  };

  const toggleStrategy = (strategy) => {
    setFormData(prev => ({
      ...prev,
      strategies_used: prev.strategies_used.includes(strategy)
        ? prev.strategies_used.filter(s => s !== strategy)
        : [...prev.strategies_used, strategy]
    }));
  };

  const addCustomStrategy = () => {
    if (customStrategy.trim() && !formData.strategies_used.includes(customStrategy.trim())) {
      setFormData(prev => ({
        ...prev,
        strategies_used: [...prev.strategies_used, customStrategy.trim()]
      }));
      setCustomStrategy('');
    }
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      setFormData(prev => ({
        ...prev,
        goals_addressed: [...prev.goals_addressed, newGoal.trim()]
      }));
      setNewGoal('');
    }
  };

  const removeGoal = (index) => {
    setFormData(prev => ({
      ...prev,
      goals_addressed: prev.goals_addressed.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Feb 2026 (Iter 102) — hidden file input mounted at component root
          so it's always in the DOM, no matter whether the therapist adds
          photos from inside the SOAP form or from the Share dialog. The
          previous location (inside the Share dialog JSX) left the ref
          null while the dialog was closed, so clicking "Add photos" from
          the SOAP form silently did nothing after consent. */}
      <input
        ref={photoFileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp"
        multiple
        className="hidden"
        onChange={handlePhotoFilesSelected}
        data-testid="session-photos-file-input"
      />
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button variant="ghost" onClick={() => navigate(`/children/${childId}`)} className="rounded-full p-2 sm:p-2.5">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-serif text-primary-900">Session Notes</h1>
              <p className="text-xs sm:text-sm text-stone-500">{child?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!showForm && notes.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectMode(!selectMode);
                    if (selectMode) setSelectedNotes(new Set());
                  }}
                  className="rounded-full text-xs sm:text-sm"
                  data-testid="toggle-select-mode"
                >
                  {selectMode ? <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
                  <span className="hidden xs:inline">{selectMode ? 'Cancel' : 'Select'}</span>
                </Button>
                {selectMode && selectedNotes.size > 0 && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadSelectedNotes}
                      className="rounded-full text-xs sm:text-sm"
                      data-testid="export-selected-btn"
                    >
                      <FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      Export ({selectedNotes.size})
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => generateMultiNoteFeedback('parent')}
                      disabled={generatingMulti}
                      className="rounded-full text-xs sm:text-sm bg-primary text-white hover:bg-primary/90"
                      data-testid="build-multi-feedback-btn"
                    >
                      {generatingMulti ? (
                        <>
                          <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                          <span>Opening…</span>
                        </>
                      ) : (
                        <>
                          <Share2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span>
                            <span className="hidden sm:inline">Share feedback / photos</span>
                            <span className="sm:hidden">Share</span>
                            {' '}({selectedNotes.size})
                          </span>
                        </>
                      )}
                    </Button>
                  </>
                )}
              </>
            )}
            {!showForm && (
              <Button onClick={openNewSessionForm} className="rounded-full text-xs sm:text-sm" data-testid="new-session-btn">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Session Note</span>
                <span className="sm:hidden">New</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {showForm ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 bg-white rounded-3xl border border-stone-100">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-serif text-primary-900">
                      {editingNote ? 'Edit Session Note' : draftId ? 'Continue Draft' : 'New Session Note'}
                    </h2>
                    {/* Auto-save indicator */}
                    {!editingNote && (
                      <div className="flex items-center gap-2 mt-1">
                        {savingDraft && (
                          <span className="flex items-center gap-1 text-xs text-stone-400">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Saving...
                          </span>
                        )}
                        {lastSaved && !savingDraft && (
                          <span className="text-xs text-green-600">
                            Draft saved at {lastSaved.toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!editingNote && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={saveDraftManually}
                        disabled={savingDraft}
                        className="rounded-full"
                        data-testid="save-draft-btn"
                      >
                        <PauseCircle className="w-4 h-4 mr-1" />
                        Save & Exit
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); resetForm(); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Session Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                        className="h-11 rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={formData.session_time || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, session_time: e.target.value }))}
                        className="h-11 rounded-xl"
                        data-testid="session-time-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Session Type</Label>
                      <Select value={formData.session_type} onValueChange={(v) => setFormData(prev => ({ ...prev, session_type: v }))}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SESSION_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (mins)</Label>
                      <Input
                        type="number"
                        min="5"
                        max="180"
                        value={formData.session_duration ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Allow the field to be temporarily empty so the user
                          // can clear it and type a fresh value. We coerce to
                          // an int on blur (below). Falling back to 30 inside
                          // onChange traps the cursor at "30" / "3".
                          setFormData((prev) => ({
                            ...prev,
                            session_duration: raw === '' ? '' : parseInt(raw, 10),
                          }));
                        }}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setFormData((prev) => ({
                            ...prev,
                            session_duration: Number.isFinite(n) && n >= 5 ? Math.min(n, 180) : 30,
                          }));
                        }}
                        className="h-11 rounded-xl"
                        data-testid="session-duration-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Select value={formData.session_location} onValueChange={(v) => setFormData(prev => ({ ...prev, session_location: v }))}>
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SESSION_LOCATIONS.map(l => (
                            <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Professional Registration Number */}
                  <div className="flex items-center gap-4 p-3 bg-stone-50 rounded-xl">
                    <User className="w-5 h-5 text-stone-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-stone-700">Professional Registration</p>
                      {therapistRegNumber ? (
                        <p className="text-sm text-stone-500">{therapistRegNumber}</p>
                      ) : (
                        <p className="text-sm text-stone-400">Not set</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTempRegNumber(therapistRegNumber);
                        setShowRegNumberDialog(true);
                      }}
                      className="rounded-full"
                    >
                      {therapistRegNumber ? 'Edit' : 'Add'}
                    </Button>
                  </div>

                  {/* Focus Areas - Multi-select */}
                  <div className="space-y-3">
                    <Label>Focus Area(s)</Label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => toggleCategory(cat.value)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            formData.categories?.includes(cat.value)
                              ? 'bg-primary text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                          data-testid={`category-${cat.value}`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                    {formData.categories?.includes('other') && (
                      <Input
                        value={formData.other_category}
                        onChange={(e) => setFormData(prev => ({ ...prev, other_category: e.target.value }))}
                        placeholder="Specify other focus area..."
                        className="h-10 rounded-xl mt-2"
                        data-testid="other-category-input"
                      />
                    )}
                  </div>

                  {/* SOAP Notes */}
                  <Accordion type="multiple" defaultValue={['soap', 'clinical']} className="space-y-2">
                    <AccordionItem value="soap" className="border rounded-xl px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">Session Notes (SOAP Format)</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        {/* Privacy reminder */}
                        <p className="text-xs text-stone-400 italic">
                          Please avoid surnames or identifiable personal details.
                        </p>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">S</span>
                            Subjective
                          </Label>
                          <Textarea
                            value={formData.subjective}
                            onChange={(e) => setFormData(prev => ({ ...prev, subjective: e.target.value }))}
                            placeholder="Parent/caregiver report, child's mood, relevant events..."
                            className="min-h-20 rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center">O</span>
                            Objective
                          </Label>
                          <Textarea
                            value={formData.objective}
                            onChange={(e) => setFormData(prev => ({ ...prev, objective: e.target.value }))}
                            placeholder="What was done, activities used, data collected, observations..."
                            className="min-h-24 rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">A</span>
                            Assessment
                          </Label>
                          <Textarea
                            value={formData.assessment}
                            onChange={(e) => setFormData(prev => ({ ...prev, assessment: e.target.value }))}
                            placeholder="Clinical interpretation, progress toward goals, response to treatment..."
                            className="min-h-20 rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">P</span>
                            Plan
                          </Label>
                          <Textarea
                            value={formData.plan}
                            onChange={(e) => setFormData(prev => ({ ...prev, plan: e.target.value }))}
                            placeholder="Next steps, homework, recommendations, goals for next session..."
                            className="min-h-20 rounded-xl"
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="clinical" className="border rounded-xl px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">Clinical Data</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Goals Addressed</Label>
                            {!editingNote && (
                              <label className="flex items-center gap-2 text-sm text-stone-500 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={carryOverGoals}
                                  onChange={(e) => setCarryOverGoals(e.target.checked)}
                                  className="rounded border-stone-300 text-primary focus:ring-primary"
                                  data-testid="auto-carry-over-checkbox"
                                />
                                Auto carry-over goals
                              </label>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={newGoal}
                              onChange={(e) => setNewGoal(e.target.value)}
                              placeholder="Add a goal..."
                              className="h-10 rounded-xl"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGoal())}
                            />
                            <Button type="button" variant="outline" onClick={addGoal} className="rounded-xl">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Suggested goals from the child's profile, filtered to the
                              focus areas selected at the top of this SOAP form.
                              Feb 2026 — Uncategorised goals are now HIDDEN when a
                              focus area is active (previous behaviour showed
                              everything and defeated the filter — Nelanda's bug).
                              A toggle below the list opens the full unfiltered
                              set so no legitimate goal is unreachable. */}
                          {(() => {
                            const profileGoals = Array.isArray(child?.therapy_goals) ? child.therapy_goals : [];
                            const seen = new Set();
                            const allGoals = [...profileGoals, ...carriedGoals].filter((g) => {
                              const k = (g || '').trim().toLowerCase();
                              if (!k || seen.has(k)) return false;
                              seen.add(k);
                              return true;
                            });
                            if (allGoals.length === 0) return null;
                            const cats = formData.categories || [];
                            const noSelection = cats.length === 0;
                            // 'general' sessions are not tied to a focus area → no filter
                            const noFilter = cats.includes('general');
                            const allowedCategories = new Set(
                              cats.flatMap((c) => SOAP_TO_GOAL_CATEGORIES[c] || [])
                            );
                            const goalCategoryMap = child?.goal_categories || {};
                            const normCats = {};
                            Object.entries(goalCategoryMap).forEach(([k, v]) => { normCats[k.trim().toLowerCase()] = v; });
                            const catOf = (g) => goalCategoryMap[g] || normCats[(g || '').trim().toLowerCase()];
                            const selectedKeys = new Set(formData.goals_addressed.map((g) => (g || '').trim().toLowerCase()));
                            const isSelected = (g) => selectedKeys.has((g || '').trim().toLowerCase());
                            const inScope = (g) => {
                              if (noFilter) return true;
                              if (noSelection) return false;
                              const cat = catOf(g);
                              return !!cat && allowedCategories.has(cat); // uncategorised → only in See All
                            };
                            const relevant = allGoals.filter(inScope);
                            const pool = showAllProfileGoals ? allGoals : relevant;
                            const suggested = pool.filter((g) => !isSelected(g));
                            const hiddenCount = showAllProfileGoals ? 0 : allGoals.filter((g) => !inScope(g) && !isSelected(g)).length;
                            let emptyMsg = null;
                            if (!showAllProfileGoals && noSelection) emptyMsg = 'Select a focus area to view relevant goals';
                            else if (!showAllProfileGoals && relevant.length === 0) emptyMsg = 'No goals assigned to this focus area';
                            else if (suggested.length === 0) emptyMsg = 'All of these goals are already added below';
                            return (
                              <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="suggested-goals-panel">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <p className="text-xs uppercase tracking-wider text-indigo-700 font-semibold">
                                    {showAllProfileGoals ? 'All goals' : 'Relevant goals'}
                                    {!showAllProfileGoals && !noFilter && !noSelection && (
                                      <span className="ml-2 text-[10px] font-normal text-indigo-500 normal-case tracking-normal">
                                        · filtered to today&apos;s focus
                                      </span>
                                    )}
                                  </p>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setShowAllProfileGoals((v) => !v)}
                                      className="text-xs h-7 border-indigo-200 text-indigo-700 bg-white"
                                      data-testid="toggle-show-all-profile-goals"
                                    >
                                      {showAllProfileGoals ? 'Show Relevant Goals' : `See All Goals (${allGoals.length})`}
                                    </Button>
                                    {suggested.length > 0 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setFormData((prev) => ({
                                          ...prev,
                                          goals_addressed: [...prev.goals_addressed, ...suggested],
                                        }))}
                                        className="text-xs h-7 text-indigo-700"
                                        data-testid="add-all-suggested-goals-btn"
                                      >
                                        Add all
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {emptyMsg && (
                                  <p className="text-xs text-stone-600 py-1" data-testid="goals-filter-empty-msg">{emptyMsg}</p>
                                )}
                                <div className="flex flex-wrap gap-1.5">
                                  {suggested.map((g, i) => {
                                    const cat = catOf(g);
                                    return (
                                      <button
                                        type="button"
                                        key={g}
                                        onClick={() =>
                                          setFormData((prev) => ({
                                            ...prev,
                                            goals_addressed: [...prev.goals_addressed, g],
                                          }))
                                        }
                                        className="text-xs bg-white border border-indigo-200 rounded-full px-2.5 py-1 hover:bg-indigo-100 transition-colors flex items-center gap-1"
                                        data-testid={`suggested-goal-${i}`}
                                        title={cat ? `Category: ${cat}` : 'Uncategorised'}
                                      >
                                        <Plus className="w-3 h-3 text-indigo-500" />
                                        <span className="text-stone-800">{g}</span>
                                        {!cat && showAllProfileGoals && <span className="text-[10px] text-stone-400">· uncategorised</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                                {hiddenCount > 0 && !noSelection && (
                                  <p className="mt-2 text-[10px] text-stone-500" data-testid="goals-hidden-count">
                                    {hiddenCount} other goal{hiddenCount === 1 ? '' : 's'} hidden (other focus areas or uncategorised).
                                  </p>
                                )}
                              </div>
                            );
                          })()}

                          {/* Per-Goal Progress Tracking */}
                          {formData.goals_addressed.length > 0 && (
                            <div className="space-y-3 mt-3">
                              {formData.goals_addressed.map((goal, i) => {
                                const gp = formData.goal_progress?.[goal] || {};
                                return (
                                  <div key={i} className="p-3 bg-stone-50 rounded-xl border border-stone-100 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className="text-sm font-medium text-stone-800 flex-1">{goal}</p>
                                      <button type="button" onClick={() => removeGoal(i)} className="text-stone-400 hover:text-red-500 shrink-0 p-0.5">
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label className="text-xs text-stone-500">Progress</Label>
                                        <Select
                                          value={typeof gp === 'string' ? gp : (gp.status || 'emerging')}
                                          onValueChange={(v) => setFormData(prev => ({
                                            ...prev,
                                            goal_progress: { ...prev.goal_progress, [goal]: { ...(typeof prev.goal_progress?.[goal] === 'object' ? prev.goal_progress[goal] : {}), status: v } }
                                          }))}
                                        >
                                          <SelectTrigger className="h-8 rounded-lg text-xs" data-testid={`goal-progress-${i}`}>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="not_addressed">Not addressed</SelectItem>
                                            <SelectItem value="emerging">Emerging</SelectItem>
                                            <SelectItem value="progressing">Progressing</SelectItem>
                                            <SelectItem value="mastered">Mastered</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-stone-500">Prompting</Label>
                                        <Select
                                          value={gp.prompting || 'none'}
                                          onValueChange={(v) => setFormData(prev => ({
                                            ...prev,
                                            goal_progress: { ...prev.goal_progress, [goal]: { ...(typeof prev.goal_progress?.[goal] === 'object' ? prev.goal_progress[goal] : {}), prompting: v } }
                                          }))}
                                        >
                                          <SelectTrigger className="h-8 rounded-lg text-xs" data-testid={`goal-prompting-${i}`}>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {PROMPTING_LEVELS.map(p => (
                                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-xs text-stone-500">Accuracy / Notes</Label>
                                      <Input
                                        value={gp.accuracy || ''}
                                        onChange={(e) => setFormData(prev => ({
                                          ...prev,
                                          goal_progress: { ...prev.goal_progress, [goal]: { ...(typeof prev.goal_progress?.[goal] === 'object' ? prev.goal_progress[goal] : {}), accuracy: e.target.value } }
                                        }))}
                                        placeholder="e.g., 4/6 opportunities (67%)"
                                        className="h-8 rounded-lg text-xs"
                                        data-testid={`goal-accuracy-${i}`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* View Previous Goals Button */}
                          <div className="mt-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={fetchPreviousGoals}
                              disabled={loadingPreviousGoals}
                              className="rounded-full text-sm"
                              data-testid="view-previous-goals-btn"
                            >
                              {loadingPreviousGoals ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <History className="w-4 h-4 mr-2" />
                              )}
                              View All Previous Goals
                            </Button>
                          </div>
                          
                          {/* Previous Goals Display */}
                          {showPreviousGoals && previousGoals && (
                            <div className="mt-3 p-4 bg-amber-50 rounded-xl border border-amber-200" data-testid="previous-goals-panel">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <History className="w-4 h-4 text-amber-600" />
                                  <span className="text-sm font-medium text-amber-800">
                                    Previous Session Goals
                                    {previousGoals.date && (
                                      <span className="text-amber-600 font-normal ml-2">
                                        ({new Date(previousGoals.date).toLocaleDateString()})
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setShowPreviousGoals(false)}
                                  className="text-amber-600 hover:text-amber-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              
                              {previousGoals.has_previous ? (
                                <>
                                  {previousGoals.goals?.length > 0 ? (
                                    <>
                                      <div className="flex flex-wrap gap-2 mb-3">
                                        {previousGoals.goals.map((goal, i) => (
                                          <div key={i} className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-full border border-amber-200">
                                            <span className="text-sm text-stone-700">{goal}</span>
                                            <button
                                              type="button"
                                              onClick={() => copyGoalToForm(goal)}
                                              className="ml-1 text-amber-600 hover:text-amber-800"
                                              title="Add to current session"
                                            >
                                              <Copy className="w-3 h-3" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={copyAllGoalsToForm}
                                        className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                                      >
                                        <Copy className="w-3 h-3 mr-1" /> Copy All Goals
                                      </Button>
                                    </>
                                  ) : (
                                    <p className="text-sm text-amber-600">No specific goals were recorded in the previous session.</p>
                                  )}
                                  
                                  {previousGoals.plan && (
                                    <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                                      <p className="text-xs font-medium text-amber-700 mb-1">Previous Plan/Next Steps:</p>
                                      <p className="text-sm text-stone-700">{previousGoals.plan}</p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-amber-600">No previous session notes found for this child.</p>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Strategies Used</Label>
                          <div className="flex flex-wrap gap-2">
                            {COMMON_STRATEGIES.map(strategy => (
                              <button
                                key={strategy}
                                type="button"
                                onClick={() => toggleStrategy(strategy)}
                                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                  formData.strategies_used.includes(strategy)
                                    ? 'bg-primary text-white'
                                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                              >
                                {strategy}
                              </button>
                            ))}
                          </div>
                          {/* Custom strategy input when Other is selected */}
                          {formData.strategies_used.includes('Other') && (
                            <div className="flex gap-2 mt-2">
                              <Input
                                value={customStrategy}
                                onChange={(e) => setCustomStrategy(e.target.value)}
                                placeholder="Specify other strategy..."
                                className="h-10 rounded-xl"
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomStrategy())}
                                data-testid="custom-strategy-input"
                              />
                              <Button type="button" variant="outline" onClick={addCustomStrategy} className="rounded-xl">
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {/* Show non-predefined strategies that were added */}
                          {formData.strategies_used.filter(s => !COMMON_STRATEGIES.includes(s)).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {formData.strategies_used.filter(s => !COMMON_STRATEGIES.includes(s)).map((strategy, i) => (
                                <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1 rounded-full bg-amber-100 text-amber-800">
                                  {strategy}
                                  <button 
                                    type="button" 
                                    onClick={() => toggleStrategy(strategy)} 
                                    className="ml-2 hover:text-red-500"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Materials Used</Label>
                          <div className="flex gap-2">
                            <Input
                              value={newMaterial}
                              onChange={(e) => setNewMaterial(e.target.value)}
                              placeholder="Add material..."
                              className="h-10 rounded-xl"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMaterial())}
                            />
                            <Button type="button" variant="outline" onClick={addMaterial} className="rounded-xl">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {formData.materials_used.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {formData.materials_used.map((material, i) => (
                                <Badge key={i} variant="outline" className="pl-3 pr-1 py-1 rounded-full">
                                  {material}
                                  <button type="button" onClick={() => removeMaterial(i)} className="ml-2 hover:text-red-500">
                                    <X className="w-3 h-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="billing" className="border rounded-xl px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium flex items-center gap-2">
                          Treatment/Billing Codes
                          <Badge variant="outline" className="text-xs font-normal">EMR Ready</Badge>
                          {(formData.treatment_codes?.length > 0 || formData.cpt_code) && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs ml-1">
                              {formData.treatment_codes?.length || (formData.cpt_code ? 1 : 0)}
                            </Badge>
                          )}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <p className="text-xs text-stone-500">
                          Add procedure codes for your region (CPT, ICD-10 procedure, MBS, etc.). These will be included when you copy notes for your EMR.
                        </p>
                        
                        {/* Treatment Codes - Multi-entry */}
                        <div className="space-y-2">
                          <Label>Treatment/Procedure Codes</Label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(formData.treatment_codes || []).map((code, idx) => (
                              <Badge key={idx} className="bg-purple-100 text-purple-800 pl-3 pr-1 py-1.5 rounded-full">
                                <span className="font-mono">{code}</span>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    treatment_codes: formData.treatment_codes.filter((_, i) => i !== idx)
                                  })}
                                  className="ml-2 text-purple-500 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              id="new-treatment-code"
                              placeholder="e.g., 92507"
                              className="h-10 rounded-xl flex-1"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                  e.preventDefault();
                                  const newCode = e.target.value.trim().toUpperCase();
                                  if (!formData.treatment_codes?.includes(newCode)) {
                                    setFormData({
                                      ...formData,
                                      treatment_codes: [...(formData.treatment_codes || []), newCode]
                                    });
                                  }
                                  e.target.value = '';
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                              onClick={() => {
                                const input = document.getElementById('new-treatment-code');
                                if (input && input.value.trim()) {
                                  const newCode = input.value.trim().toUpperCase();
                                  if (!formData.treatment_codes?.includes(newCode)) {
                                    setFormData({
                                      ...formData,
                                      treatment_codes: [...(formData.treatment_codes || []), newCode]
                                    });
                                  }
                                  input.value = '';
                                }
                              }}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-stone-400">Common: 92507 (speech tx), 92508 (group), 97530 (activities). Press Enter to add.</p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Billing Notes</Label>
                          <Input
                            value={formData.billing_notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_notes: e.target.value }))}
                            placeholder="Units, modifiers, etc."
                            className="h-10 rounded-xl"
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="parent" className="border rounded-xl px-4 border-blue-200 bg-blue-50/30">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                          Parent Feedback (Optional)
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <p className="text-sm text-stone-600">
                            Write a simplified, parent-friendly summary of this session.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={generateParentSummary}
                            disabled={generatingSummary || (!formData.subjective && !formData.objective && !formData.assessment && !formData.plan)}
                            className="rounded-full text-purple-700 border-purple-200 hover:bg-purple-50 shrink-0 w-full sm:w-auto"
                            data-testid="generate-parent-summary-btn"
                          >
                            {generatingSummary ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Generate Summary
                              </>
                            )}
                          </Button>
                        </div>
                        <Textarea
                          value={formData.parent_summary}
                          onChange={(e) => setFormData(prev => ({ ...prev, parent_summary: e.target.value }))}
                          placeholder="Today we worked on... Your child did great with... At home, you can try..."
                          className="min-h-24 rounded-xl bg-white"
                        />
                        {showParentFeedback && (
                          <AIFeedbackPopup type="parent_summary" context="Parent feedback generation from SOAP note" onDismiss={() => setShowParentFeedback(false)} />
                        )}
                        <div className="flex items-center justify-between p-3 bg-white rounded-xl">
                          <div className="flex items-center gap-3">
                            <Share2 className="w-5 h-5 text-blue-600" />
                            <div>
                              <Label htmlFor="share-toggle" className="text-sm font-medium cursor-pointer">
                                Share with parent
                              </Label>
                              <p className="text-xs text-stone-500">Parent will see the summary above</p>
                            </div>
                          </div>
                          <Switch
                            id="share-toggle"
                            checked={formData.shared_with_parent}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, shared_with_parent: checked }))}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Feb 2026 — Session Photo Sharing: in-form entry point.
                      Photos uploaded here are staged (not yet shared with
                      the parent); they'll appear automatically in the
                      "Share feedback / photos" dialog when the therapist
                      is ready to send. No photo bytes are ever stored on
                      the SOAP note itself. */}
                  {!editingNote && (
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4" data-testid="soap-form-photos-tray">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Camera className="w-4 h-4 text-primary" />
                          <h4 className="text-sm font-medium text-stone-900">Add session photos</h4>
                          <span className="text-xs text-stone-500">(optional · up to 5)</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-full h-8 text-xs"
                          onClick={onAddPhotosClick}
                          disabled={uploadingPhoto || sessionPhotos.length >= 5}
                          data-testid="soap-form-photos-add-btn"
                        >
                          {uploadingPhoto ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
                          ) : (
                            <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add photos</>
                          )}
                        </Button>
                      </div>
                      {sessionPhotos.length === 0 ? (
                        <p className="text-xs text-stone-500">
                          Attach photos from this session. They stay private until you share them from the "Share feedback / photos" dialog.
                        </p>
                      ) : (
                        <>
                          <ul className="flex flex-wrap gap-2" data-testid="soap-form-photos-list">
                            {sessionPhotos.map((p) => (
                              <li
                                key={p.id}
                                className="relative w-20 h-20 rounded-xl overflow-hidden border border-stone-200 bg-white group"
                                data-testid={`soap-form-photo-${p.id}`}
                              >
                                {p.previewUrl ? (
                                  <img src={p.previewUrl} alt="session photo preview" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                                    <ImageIcon className="w-6 h-6" />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeStagedPhoto(p.id)}
                                  className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                  aria-label="Remove photo"
                                  data-testid={`soap-form-photo-remove-${p.id}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[11px] text-stone-500 mt-2">
                            {sessionPhotos.length} photo{sessionPhotos.length === 1 ? '' : 's'} staged. Open "Share feedback / photos" after saving to send to the parent.
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 pb-20">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowForm(false); resetForm(); }}
                      className="flex-1 h-12 rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} className="flex-1 h-12 rounded-full">
                      {saving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> {editingNote ? 'Update Note' : 'Save Note'}</>
                      )}
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {/* Drafts Section */}
              {notes.filter(n => n.is_draft).length > 0 && (
                <Card className="p-4 bg-amber-50 rounded-2xl border border-amber-200" data-testid="drafts-section">
                  <div className="flex items-center gap-2 mb-3">
                    <PauseCircle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-medium text-amber-800">Drafts</h3>
                    <Badge className="bg-amber-200 text-amber-800 text-xs">
                      {notes.filter(n => n.is_draft).length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {notes.filter(n => n.is_draft).map(draft => (
                      <div 
                        key={draft.id} 
                        className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-amber-600" />
                          <div>
                            <p className="text-sm font-medium text-stone-700">
                              Draft from {new Date(draft.date).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-stone-500">
                              Last saved: {new Date(draft.updated_at || draft.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => resumeDraft(draft)}
                          className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                          data-testid={`resume-draft-${draft.id}`}
                        >
                          <PlayCircle className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Goal Progress Stats */}
              {goalStats && goalStats.goal_stats.length > 0 && (
                <Card className="p-4 bg-white rounded-2xl border border-stone-100" data-testid="goal-stats-section">
                  <button
                    onClick={() => setShowGoalStats(!showGoalStats)}
                    className="flex items-center justify-between w-full"
                  >
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      <h3 className="font-medium text-primary-900">Goal Progress Tracking</h3>
                      <Badge className="bg-primary/10 text-primary text-xs">
                        {goalStats.goal_stats.length} goals
                      </Badge>
                    </div>
                    {showGoalStats ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  
                  {showGoalStats && (
                    <div className="mt-4 space-y-3">
                      <p className="text-xs text-stone-500">
                        Based on {goalStats.total_sessions} session{goalStats.total_sessions !== 1 ? 's' : ''}
                      </p>
                      {goalStats.goal_stats.slice(0, 10).map((stat, index) => (
                        <div key={index} className="p-3 bg-stone-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-stone-700">{stat.goal}</span>
                            <span className="text-xs text-stone-500">
                              Last: {stat.last_addressed ? new Date(stat.last_addressed).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-stone-500">All time</span>
                                <span className="font-medium">{stat.total_count}/{stat.total_sessions} ({stat.percentage}%)</span>
                              </div>
                              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full transition-all"
                                  style={{ width: `${stat.percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-stone-500">Recent ({goalStats.recent_sessions})</span>
                                <span className="font-medium">{stat.recent_count}/{goalStats.recent_sessions} ({stat.recent_percentage}%)</span>
                              </div>
                              <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full transition-all"
                                  style={{ width: `${stat.recent_percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {goalStats.goal_stats.length > 10 && (
                        <p className="text-xs text-stone-400 text-center">
                          Showing top 10 of {goalStats.goal_stats.length} goals
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Select All bar */}
              {selectMode && notes.filter(n => !n.is_draft).length > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <button
                    onClick={selectAllNotes}
                    className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800"
                  >
                    {selectedNotes.size === notes.filter(n => !n.is_draft).length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    {selectedNotes.size === notes.filter(n => !n.is_draft).length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-blue-600">
                    {selectedNotes.size} of {notes.filter(n => !n.is_draft).length} selected
                  </span>
                </div>
              )}

              {notes.filter(n => !n.is_draft).length === 0 ? (
                <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
                  <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-700 mb-2">No session notes yet</h3>
                  <p className="text-stone-500 mb-6">Create your first session note to start documenting</p>
                  <Button onClick={() => setShowForm(true)} className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Create Session Note
                  </Button>
                </Card>
              ) : (
                notes.filter(n => !n.is_draft).map((note) => {
                  const isExpanded = expandedNotes[note.id];
                  const noteCategories = note.categories || [note.category];
                  const primaryCategory = CATEGORIES.find(c => c.value === noteCategories[0]) || CATEGORIES[0];
                  const isSelected = selectedNotes.has(note.id);
                  
                  return (
                    <Card
                      key={note.id}
                      className={`bg-white rounded-2xl border overflow-hidden transition-colors ${
                        isSelected ? 'border-blue-400 bg-blue-50/30' : 'border-stone-100'
                      }`}
                    >
                      <div
                        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                        onClick={() => {
                          if (selectMode) {
                            toggleNoteSelection(note.id);
                          } else {
                            setExpandedNotes(prev => ({ ...prev, [note.id]: !prev[note.id] }));
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            {selectMode && (
                              <div className="pt-2">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleNoteSelection(note.id)}
                                  className="data-[state=checked]:bg-blue-600"
                                />
                              </div>
                            )}
                            <div className="text-center">
                              <div className="w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                                <span className="text-lg font-bold text-primary">
                                  {new Date(note.date).getDate()}
                                </span>
                                <span className="text-xs text-stone-500">
                                  {new Date(note.date).toLocaleDateString('en-US', { month: 'short' })}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                {noteCategories.map((cat, idx) => {
                                  const catObj = CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
                                  return (
                                    <Badge key={idx} className={`${catObj.color} border-0`}>
                                      {catObj.label}
                                    </Badge>
                                  );
                                })}
                                {note.other_category && (
                                  <Badge className="bg-gray-100 text-gray-700 border-0">
                                    {note.other_category}
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {note.session_duration || 30} min
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {SESSION_LOCATIONS.find(l => l.value === note.session_location)?.label || 'Clinic'}
                                </Badge>
                                {note.shared_with_parent && (
                                  <Badge className="bg-green-100 text-green-700 border-0">
                                    <Eye className="w-3 h-3 mr-1" /> Shared
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-stone-600 line-clamp-2">
                                {note.objective || note.observation || 'No notes recorded'}
                              </p>
                            </div>
                          </div>
                          {!selectMode && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); downloadNote(note); }}
                                className="rounded-full"
                                title="Download/Print"
                                data-testid={`download-note-${note.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); startEdit(note); }}
                                className="rounded-full"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                                className="rounded-full text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <AnimatePresence>
                        {isExpanded && !selectMode && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 pt-0 border-t border-stone-100">
                              {/* EMR Copy Buttons */}
                              <div className="flex items-center justify-between mt-4 mb-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-purple-600" />
                                  <span className="text-sm font-medium text-purple-800">EMR Export</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const sessionType = SESSION_TYPES.find(t => t.value === note.session_type)?.label || note.session_type;
                                      const location = SESSION_LOCATIONS.find(l => l.value === note.session_location)?.label || note.session_location;
                                      const fullNote = [
                                        `SESSION NOTE - ${new Date(note.date).toLocaleDateString()}`,
                                        `Type: ${sessionType} | Duration: ${note.session_duration || 30} min | Location: ${location}`,
                                        note.session_time ? `Time: ${note.session_time}` : '',
                                        '',
                                        note.subjective ? `SUBJECTIVE:\n${note.subjective}` : '',
                                        note.objective || note.observation ? `\nOBJECTIVE:\n${note.objective || note.observation}` : '',
                                        note.assessment ? `\nASSESSMENT:\n${note.assessment}` : '',
                                        note.plan ? `\nPLAN:\n${note.plan}` : '',
                                        note.goals_addressed?.length > 0 ? `\nGOALS ADDRESSED:\n${note.goals_addressed.join(', ')}` : '',
                                        note.strategies_used?.length > 0 ? `\nSTRATEGIES: ${note.strategies_used.join(', ')}` : '',
                                        note.accuracy_data ? `\nACCURACY DATA: ${note.accuracy_data}` : '',
                                        note.prompting_level && note.prompting_level !== 'none' ? `PROMPTING: ${note.prompting_level}` : '',
                                        note.treatment_codes?.length > 0 ? `\nTREATMENT CODES: ${note.treatment_codes.join(', ')}` : (note.cpt_code ? `\nTREATMENT CODE: ${note.cpt_code}` : ''),
                                        note.billing_notes ? `BILLING NOTES: ${note.billing_notes}` : ''
                                      ].filter(Boolean).join('\n');
                                      navigator.clipboard.writeText(fullNote);
                                      toast.success('Full SOAP note copied for EMR!');
                                    }}
                                    className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-100"
                                    data-testid={`copy-full-note-${note.id}`}
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy Full Note
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-4">
                                {note.subjective && (
                                  <div className="p-3 bg-blue-50 rounded-xl group relative">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-medium text-blue-700">Subjective</p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(`S: ${note.subjective}`);
                                          toast.success('Subjective copied!');
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-blue-100"
                                        title="Copy Subjective"
                                      >
                                        <Copy className="w-3 h-3 text-blue-600" />
                                      </button>
                                    </div>
                                    <p className="text-sm text-stone-700">{note.subjective}</p>
                                  </div>
                                )}
                                {(note.objective || note.observation) && (
                                  <div className="p-3 bg-green-50 rounded-xl group relative">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-medium text-green-700">Objective</p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(`O: ${note.objective || note.observation}`);
                                          toast.success('Objective copied!');
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-green-100"
                                        title="Copy Objective"
                                      >
                                        <Copy className="w-3 h-3 text-green-600" />
                                      </button>
                                    </div>
                                    <p className="text-sm text-stone-700">{note.objective || note.observation}</p>
                                  </div>
                                )}
                                {note.assessment && (
                                  <div className="p-3 bg-amber-50 rounded-xl group relative">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-medium text-amber-700">Assessment</p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(`A: ${note.assessment}`);
                                          toast.success('Assessment copied!');
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-amber-100"
                                        title="Copy Assessment"
                                      >
                                        <Copy className="w-3 h-3 text-amber-600" />
                                      </button>
                                    </div>
                                    <p className="text-sm text-stone-700">{note.assessment}</p>
                                  </div>
                                )}
                                {note.plan && (
                                  <div className="p-3 bg-purple-50 rounded-xl group relative">
                                    <div className="flex items-center justify-between mb-1">
                                      <p className="text-xs font-medium text-purple-700">Plan</p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(`P: ${note.plan}`);
                                          toast.success('Plan copied!');
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-purple-100"
                                        title="Copy Plan"
                                      >
                                        <Copy className="w-3 h-3 text-purple-600" />
                                      </button>
                                    </div>
                                    <p className="text-sm text-stone-700">{note.plan}</p>
                                  </div>
                                )}
                              </div>
                              
                              {(note.goals_addressed?.length > 0 || note.strategies_used?.length > 0 || note.accuracy_data) && (
                                <div className="mt-4 p-3 bg-stone-50 rounded-xl">
                                  <p className="text-xs font-medium text-stone-600 mb-2">Clinical Data</p>
                                  <div className="flex flex-wrap gap-2">
                                    {note.prompting_level && note.prompting_level !== 'none' && (
                                      <Badge variant="outline">Prompting: {note.prompting_level}</Badge>
                                    )}
                                    {note.accuracy_data && (
                                      <Badge variant="outline">Accuracy: {note.accuracy_data}</Badge>
                                    )}
                                  </div>
                                  {note.goals_addressed?.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-stone-500 mb-1">Goals:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {note.goals_addressed.map((g, i) => (
                                          <Badge key={i} variant="secondary" className="text-xs">{g}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {note.strategies_used?.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-stone-500 mb-1">Strategies:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {note.strategies_used.map((s, i) => (
                                          <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {note.parent_summary && (
                                <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-medium text-blue-700 flex items-center gap-1">
                                      <MessageSquare className="w-3 h-3" /> Parent Feedback
                                    </p>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleShare(note); }}
                                      className={`text-xs px-2 py-1 rounded-full ${
                                        note.shared_with_parent 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-stone-100 text-stone-600'
                                      }`}
                                    >
                                      {note.shared_with_parent ? 'Shared' : 'Not shared'}
                                    </button>
                                  </div>
                                  <p className="text-sm text-stone-700">{note.parent_summary}</p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  );
                })
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Registration Number Dialog */}
      <Dialog open={showRegNumberDialog} onOpenChange={setShowRegNumberDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Professional Registration Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-stone-600">
              Enter your professional registration number (e.g., HPCSA, ASHA, RCSLT). This will be saved to your profile and automatically included in all future session notes.
            </p>
            <Input
              value={tempRegNumber}
              onChange={(e) => setTempRegNumber(e.target.value)}
              placeholder="e.g., SLT 12345678"
              className="h-12 rounded-xl"
              data-testid="reg-number-input"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRegNumberDialog(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={saveTherapistRegNumber}
                className="flex-1 rounded-full"
                data-testid="save-reg-number-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-save "send feedback to parent?" prompt — opens automatically
          right after a fresh SOAP save with the new note pre-ticked, so the
          therapist can quickly assemble a multi-note feedback summary while
          everything is fresh. Dismissing leaves the user on the SOAP list. */}
      <Dialog
        open={postSaveShareModal.open}
        onOpenChange={(o) => setPostSaveShareModal((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="sm:max-w-md" data-testid="post-save-share-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share this session with the parent?
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600 pt-1">
              Note saved. Would you like to send the parent a written feedback summary,
              session photos, or both? This note will be pre-selected — you can add or
              remove others, and the share dialog has an "Add photos" option too.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={() => setPostSaveShareModal({ open: false, noteId: null })}
              data-testid="post-save-share-skip"
            >
              Not now
            </Button>
            <Button
              className="w-full sm:w-auto"
              data-testid="post-save-share-confirm"
              onClick={() => {
                const id = postSaveShareModal.noteId;
                setPostSaveShareModal({ open: false, noteId: null });
                setSelectMode(true);
                if (id) {
                  setSelectedNotes((prev) => {
                    const next = new Set(prev);
                    next.add(id);
                    return next;
                  });
                }
                // Smooth scroll to the top so the share toolbar is visible.
                if (typeof window !== 'undefined') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              Share feedback / photos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity suggestions modal — surfaces right after a SOAP note is saved
          so the therapist can jump straight into a Weekly Focus while context is fresh. */}
      <Dialog
        open={suggestModal.open}
        onOpenChange={(o) => setSuggestModal((s) => ({ ...s, open: o }))}
      >
        <DialogContent className="sm:max-w-2xl" data-testid="suggest-activities-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Activities that match this session
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600">
              Hand-picked from your activity bank based on the SOAP note you just saved.
              Tap any to build this week's focus for the parent.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-96 overflow-y-auto py-2">
            {suggestModal.items.length === 0 && (
              <p className="text-sm text-stone-500 text-center py-6">
                No matching activities in your bank yet. Add some on the Activity Library page.
              </p>
            )}
            {suggestModal.items.map((s, i) => {
              const act = s.activity;
              return (
                <button
                  key={act.id || i}
                  onClick={() => {
                    setSuggestModal({ open: false, items: [], childId: null });
                    navigate(
                      `/weekly-focus/${suggestModal.childId}?activity=${encodeURIComponent(act.id)}`
                    );
                  }}
                  className="w-full text-left p-3 rounded-2xl border border-stone-200 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                  data-testid={`suggested-activity-${act.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 line-clamp-1">{act.title}</p>
                      {act.description && (
                        <p className="text-sm text-stone-600 line-clamp-2 mt-0.5">
                          {act.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(act.communication_functions || []).slice(0, 3).map((cf) => (
                          <span
                            key={cf}
                            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-600"
                          >
                            {cf}
                          </span>
                        ))}
                      </div>
                    </div>
                    {s.score > 0 && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider text-primary px-2 py-1 bg-primary/10 rounded-full shrink-0"
                        title={`Match score: ${s.score}`}
                      >
                        Strong match
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setSuggestModal({ open: false, items: [], childId: null })}
              data-testid="suggest-modal-skip-btn"
            >
              Skip for now
            </Button>
            <Button
              onClick={() => {
                setSuggestModal({ open: false, items: [], childId: null });
                navigate(`/weekly-focus/${suggestModal.childId}`);
              }}
              data-testid="suggest-modal-open-weekly-btn"
            >
              Open Weekly Focus →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-note Parent Feedback modal */}
      <Dialog open={showMultiFeedback} onOpenChange={setShowMultiFeedback}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="multi-feedback-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share with parent · feedback &amp; photos
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600">
              Add written feedback, session photos, or both — each sends independently.
              {multiFeedback?.date_range?.start && multiFeedback?.date_range?.end ? (
                <> Recap drawn from sessions {multiFeedback.date_range.start} → {multiFeedback.date_range.end}.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {/* Audience switcher */}
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { v: 'parent', label: 'Parent recap' },
              { v: 'whatsapp', label: 'WhatsApp-ready' },
              { v: 'team', label: 'MDT / team' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => generateMultiNoteFeedback(opt.v)}
                disabled={generatingMulti}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  multiAudience === opt.v
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-stone-600 border-stone-200 hover:border-primary/40 hover:bg-primary/5'
                }`}
                data-testid={`multi-feedback-audience-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {generatingMulti ? (
              <div className="flex items-center justify-center gap-2 py-12 text-stone-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Re-building feedback…</span>
              </div>
            ) : (
              <Textarea
                value={multiFeedback?.summary || ''}
                onChange={(e) =>
                  setMultiFeedback((prev) => (prev ? { ...prev, summary: e.target.value } : prev))
                }
                className="min-h-[260px] text-sm leading-relaxed"
                data-testid="multi-feedback-output"
              />
            )}
            {multiFeedback?.fallback && (
              <p className="text-[11px] text-amber-700 mt-2">
                AI service was unreachable — showing a deterministic recap. Try again in a moment.
              </p>
            )}
            {!(child?.parent_ids?.length) && multiFeedback?.summary && !generatingMulti && (
              <p className="text-[11px] text-stone-500 mt-2">
                No PlayFirst Parent is linked to this child yet — you can still copy this recap. Link a parent from the child profile to enable direct send.
              </p>
            )}
          </div>

          {/* Feb 2026 — Session Photo Sharing. Independent of the written
              feedback above; the Send button enables if EITHER has
              content. See routes/session_photos.py. */}
          <div className="mt-5 border-t border-stone-100 pt-4" data-testid="session-photos-tray">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-medium text-stone-900">Session photos</h4>
                <span className="text-xs text-stone-500">(optional · up to 5)</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full h-8 text-xs"
                onClick={onAddPhotosClick}
                disabled={uploadingPhoto || sessionPhotos.length >= 5}
                data-testid="session-photos-add-btn"
              >
                {uploadingPhoto ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
                ) : (
                  <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add photos</>
                )}
              </Button>
            </div>
            {sessionPhotos.length === 0 ? (
              <p className="text-xs text-stone-500">
                Share photos with the parent — independent of the written feedback above. The parent will be asked to confirm photo consent before they can view them.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2" data-testid="session-photos-list">
                {sessionPhotos.map((p) => (
                  <li
                    key={p.id}
                    className="relative w-20 h-20 rounded-xl overflow-hidden border border-stone-200 bg-stone-100 group"
                    data-testid={`session-photo-${p.id}`}
                  >
                    {p.previewUrl ? (
                      <img src={p.previewUrl} alt="session photo preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeStagedPhoto(p.id)}
                      className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      aria-label="Remove photo"
                      data-testid={`session-photo-remove-${p.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowMultiFeedback(false)}
              data-testid="multi-feedback-close-btn"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={copyMultiFeedback}
              disabled={!multiFeedback?.summary || generatingMulti}
              data-testid="multi-feedback-copy-btn"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
            <Button
              variant="outline"
              onClick={() => generateMultiNoteFeedback(multiAudience)}
              disabled={generatingMulti}
              data-testid="multi-feedback-regenerate-btn"
            >
              {generatingMulti ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-building…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" /> Re-generate
                </>
              )}
            </Button>
            <Button
              onClick={sendMultiFeedbackToParents}
              disabled={
                generatingMulti || sendingMulti || !(child?.parent_ids?.length)
                || (!(multiFeedback?.summary || '').trim() && sessionPhotos.length === 0)
              }
              data-testid="multi-feedback-send-to-parents-btn"
              title={
                !(child?.parent_ids?.length)
                  ? 'No parent linked to this child yet'
                  : 'Send feedback and/or photos to the linked parent(s)'
              }
            >
              {sendingMulti ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Send to parent
                  {child?.parent_ids?.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Session Photo Sharing consent dialog. Opens the first
          time a therapist adds a photo for a client. Not pre-ticked;
          recorded server-side with timestamp + version. */}
      <Dialog open={photoConsentDialog} onOpenChange={(o) => { setPhotoConsentDialog(o); if (!o) setPhotoConsentAck(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="photo-consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Photo sharing — confirm consent
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600 pt-1">
              Before you share photos of this client, please confirm you have appropriate consent.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-800">
            I confirm that appropriate parent/legal guardian consent has been obtained to upload and securely share session photographs of this child through PlayFirst for therapy-related purposes.
          </div>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <Checkbox
              checked={photoConsentAck}
              onCheckedChange={(c) => setPhotoConsentAck(!!c)}
              data-testid="photo-consent-ack"
            />
            <span className="text-sm text-stone-700">I confirm the statement above for this client.</span>
          </label>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="ghost" onClick={() => { setPhotoConsentDialog(false); setPhotoConsentAck(false); }} data-testid="photo-consent-cancel">
              Cancel
            </Button>
            <Button onClick={confirmPhotoConsent} disabled={!photoConsentAck} data-testid="photo-consent-confirm">
              Record consent &amp; continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
